import React from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import GetAppIcon from '@material-ui/icons/GetApp';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import { messagesRequest } from 'modules/messages/actions';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import {
  BulkClientsFields,
  EnvironmentFields,
  ListBulkClientsProps,
} from 'modules/environments/interfaces';
import i18n from 'i18n';
import palette from 'theme/palette';
import Button from 'components/Button';
import { modalClose } from 'modules/modal/actions';
import { getBulkClientsRequest } from 'modules/environments/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import styles from '../styles';
import { initialQuery } from '..';

const useStyles = makeStyles(styles);

export interface ConnectionTypeProps {
  bulkClients: ListBulkClientsProps;
  isLoadingBulkClients: boolean;
  environment: EnvironmentFields | null;
  modalClose: () => void;
  getBulkClientsRequest: (props) => void;
}

const ConnectionType = ({
  bulkClients,
  isLoadingBulkClients,
  environment,
  modalClose,
  getBulkClientsRequest,
}: ConnectionTypeProps) => {
  const classes = useStyles();

  const {
    query,
    searchValue,
    itemOrder,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const getBulkClients = () => {
    getBulkClientsRequest({
      id: environment?.environment_uuid,
      query: { ...query },
    });
  };

  React.useEffect(() => {
    getBulkClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBulkClientsRequest, query]);

  const headers = [
    {
      key: 'nome',
      i18n: 'CLIENT_NAME_KEY',
      order: true,
    },
    {
      key: 'connection_type_enabled',
      i18n: 'BULK_CLIENTS.ACTIVE_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'connection_type',
      i18n: 'BULK_CLIENTS.TYPE_KEY',
      order: true,
      centerTitle: true,
    },
  ];

  const connectionTypeEnum = {
    private: i18n.t('BULK_CLIENTS.PRIVATE_KEY'),
    public: i18n.t('BULK_CLIENTS.PUBLIC_KEY'),
  };

  const enabledEnum = {
    false: i18n.t('NO_KEY'),
    true: i18n.t('YES_KEY'),
  };

  const getClientsListPDF: Array<[
    string,
    string,
    string
  ]> = bulkClients?.results.map(item => [
    item?.name,
    enabledEnum[
      String(item?.connection_type_configuration.connection_type_enabled)
    ],
    item?.connection_type_configuration.connection_type
      ? connectionTypeEnum[item?.connection_type_configuration.connection_type]
      : '-',
  ]);

  // eslint-disable-next-line new-cap
  const doc = new jsPDF();

  const generatePDFButton = () => {
    doc.setFontSize(16);
    doc.text(
      [
        '',
        `${i18n.t('ENVIRONMENT_NAME_KEY')}: ${environment?.name}`,
        `${i18n.t('PDF_GENERATED_AT_KEY')}: ${new Date().toLocaleString(
          'pt-BR',
          {
            hour12: false,
          }
        )}`,
        `${i18n.t('CLIENTS_PAGE.LIST_CLIENTS_TITLE_KEY')}:`,
        '',
      ],
      10,
      10,
      { lineHeightFactor: 1.5, maxWidth: 200 }
    );
    autoTable(doc, {
      head: [
        [
          `${i18n.t('CLIENT_NAME_KEY')}`,
          `${i18n.t('BULK_CLIENTS.ACTIVE_KEY')}`,
          `${i18n.t('BULK_CLIENTS.TYPE_KEY')}`,
        ],
      ],
      margin: { top: 50 },
      styles: { lineWidth: 0.3, lineColor: palette.borderGray, fontSize: 11 },
      headStyles: {
        fillColor: palette.deepBlue,
        textColor: palette.offWhite,
        fontSize: 11,
      },
      body: getClientsListPDF,
    });
    doc.save(`${environment?.name}-clients-connection-type-details.pdf`);
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid container>
        <SearchBar
          id="search-client"
          isLoading={isLoadingBulkClients}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  centerTitle={item?.centerTitle}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  markOderItem={itemOrder === item?.key}
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: BulkClientsFields, index) => (
            <TableRow key={index}>
              <TableCell>{item?.name}</TableCell>
              <TableCell textAlignCenter centerTitle>
                {
                  enabledEnum[
                    String(
                      item?.connection_type_configuration
                        .connection_type_enabled
                    )
                  ]
                }
              </TableCell>
              <TableCell textAlignCenter centerTitle>
                {item?.connection_type_configuration.connection_type &&
                  connectionTypeEnum[
                    item?.connection_type_configuration.connection_type
                  ]}
              </TableCell>
            </TableRow>
          )}
          overrideRowsCount={10}
          colCount={3}
          isLoading={isLoadingBulkClients}
          data={bulkClients}
          onChangeLimit={handleLimitChange}
          onChangePage={handlePageChange}
          notFoundText={i18n.t('NO_CLIENT_KEY')}
        />
      </Grid>
      <Grid container justify="flex-end" className={classes.buttons}>
        <Button
          id="generate-pdf-button"
          submitButton
          startIcon={<GetAppIcon />}
          onClick={() => {
            generatePDFButton();
            modalClose();
            messagesRequest({
              type: 'success',
              message: i18n.t('PDF_GENERATED_SUCCESSFULLY_KEY'),
            });
          }}
        >
          {i18n.t('GENERATE_PDF')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { bulkClients, isLoadingBulkClients },
}: ApplicationState) => ({
  role: data?.role,
  bulkClients,
  isLoadingBulkClients,
});

export default connect(mapStateToProps, { modalClose, getBulkClientsRequest })(
  ConnectionType
);
